import { Component, ChangeDetectionStrategy, inject, signal, computed, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { LayoutService } from '../../core/services/layout.service';
import { PermissionService } from '../../core/services/Authentication/permission.service';
import { NavItem } from '../../core/models/navigation.model';

const ENTERPRISE_NAV_ITEMS: readonly NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'
  },
  {
    id: 'tickets',
    label: 'Tickets Workspace',
    icon: 'M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 001.105 1.79l.01.005A2 2 0 003 15v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 00-1.105-1.79l-.01-.005A2 2 0 0021 12V7a2 2 0 00-2-2H5z',
    children: [
      {
        id: 'all-tickets',
        label: 'All Tickets',
        path: '/tickets'
      },
      {
        id: 'my-tickets',
        label: 'Assigned To Me',
        path: '/my-tickets'
      },
      {
        id: 'created-by-me',
        label: 'Created By Me',
        path: '/created-by-me'
      }
    ]
  },
  {
    id: 'departments',
    label: 'Departments',
    path: '/departments',
    icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5m0 0v-5a2 2 0 012-2h2a2 2 0 012 2v5m-4 0h4'
  },
  {
    id: 'projects',
    label: 'Projects',
    path: '/projects',
    icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z'
  },
  {
    id: 'users',
    label: 'User Directory',
    path: '/users',
    icon: 'M12 4.354a4 4 0 100 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z'
  },
  {
    id: 'roles',
    label: 'Roles & Permissions',
    path: '/roles',
    icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z'
  },
  {
    id: 'sla-policies',
    label: 'SLA Policies',
    path: '/sla-policies',
    icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
  },
  {
    id: 'performance',
    label: 'Performance Analysis',
    path: '/performance',
    icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
  },
  {
    id: 'settings',
    label: 'System Settings',
    path: '/settings',
    icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z'
  }
];

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!-- Mobile Backdrop Overlay when Sidebar is open on mobile -->
    @if (!isHidden()) {
      <div
        (click)="onHide()"
        class="fixed inset-0 z-40 bg-slate-950/60 backdrop-blur-xs md:hidden animate-in fade-in">
      </div>
    }

    <aside
      [class.hidden]="isHidden()"
      [class.w-64]="!isCollapsed()"
      [class.w-20]="isCollapsed()"
      class="fixed inset-y-0 left-0 z-50 md:relative md:z-auto bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 flex flex-col h-full border-r border-slate-200 dark:border-slate-800 transition-all duration-300 ease-in-out relative group shadow-2xl md:shadow-none">
      
      <!-- Top Branding & Double Arrow Collapse Toggle (Full vs Icons-only) -->
      <div class="px-4 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
        @if (!isCollapsed()) {
          <span class="text-[11px] font-extrabold tracking-wider text-slate-500 dark:text-slate-400 uppercase">Enterprise Modules</span>
        }
        <button
          (click)="onToggleCollapse()"
          type="button"
          [title]="isCollapsed() ? 'Expand Side Nav (Full View)' : 'Collapse Side Nav (Icons Only)'"
          class="p-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer mx-auto md:mx-0">
          @if (isCollapsed()) {
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"/>
            </svg>
          } @else {
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"/>
            </svg>
          }
        </button>
      </div>

      <!-- Navigation Tree -->
      <nav class="flex-1 px-3 py-4 space-y-1.5 overflow-y-auto" aria-label="Sidebar Navigation">
        @for (item of filteredNavItems(); track item.id) {
          <div>
            @if (item.children && item.children.length > 0) {
              <!-- Expandable Parent Group -->
              <button
                (click)="toggleGroup(item.id)"
                type="button"
                [attr.aria-expanded]="isGroupExpanded(item.id)"
                class="w-full flex items-center px-3 py-2.5 rounded-xl text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all justify-between group/btn cursor-pointer">
                <div class="flex items-center space-x-3">
                  <svg class="w-5 h-5 shrink-0 text-slate-500 dark:text-slate-400 group-hover/btn:text-slate-900 dark:group-hover/btn:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="item.icon"/>
                  </svg>
                  @if (!isCollapsed()) {
                    <span>{{ item.label }}</span>
                  }
                </div>

                @if (!isCollapsed()) {
                  <div class="flex items-center space-x-2">
                    @if (item.badge) {
                      <span class="px-2 py-0.5 text-[10px] font-bold rounded-full" [class]="item.badgeColor || 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300'">
                        {{ item.badge }}
                      </span>
                    }
                    <svg
                      [class.rotate-90]="isGroupExpanded(item.id)"
                      class="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </div>
                }
              </button>

              <!-- Nested Children Options -->
              @if (isGroupExpanded(item.id) && !isCollapsed()) {
                <div class="pl-9 pr-2 py-1 space-y-1">
                  @for (child of item.children; track child.id) {
                    <a
                      [routerLink]="child.path"
                      (click)="onNavClick()"
                      routerLinkActive="bg-blue-600 text-white font-bold shadow-2xs"
                      class="block px-3 py-2 rounded-lg text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-colors">
                      {{ child.label }}
                    </a>
                  }
                </div>
              }
            } @else {
              <!-- Direct Nav Link -->
              <a
                [routerLink]="item.path"
                (click)="onNavClick()"
                routerLinkActive="bg-blue-600 text-white font-bold shadow-xs"
                [attr.title]="isCollapsed() ? item.label : null"
                class="flex items-center px-3 py-2.5 rounded-xl text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all group/link">
                <svg class="w-5 h-5 shrink-0 text-slate-500 dark:text-slate-400 group-hover/link:text-slate-900 dark:group-hover/link:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="item.icon"/>
                </svg>

                @if (!isCollapsed()) {
                  <span class="ml-3 flex-1">{{ item.label }}</span>
                  @if (item.badge) {
                    <span class="px-2 py-0.5 text-[10px] font-bold rounded-full" [class]="item.badgeColor || 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300'">
                      {{ item.badge }}
                    </span>
                  }
                }
              </a>
            }
          </div>
        }
      </nav>

      <!-- Bottom System Version -->
      @if (!isCollapsed()) {
        <div class="px-5 py-3.5 border-t border-slate-200 dark:border-slate-800 text-[10px] text-slate-500 font-mono">
          System Core v1.0.0-PROD
        </div>
      }
    </aside>
  `
})
export class SidebarComponent implements OnInit {
  private readonly layoutService = inject(LayoutService);
  private readonly permissionService = inject(PermissionService);

  readonly isCollapsed = this.layoutService.isSidebarCollapsed;
  readonly isHidden = this.layoutService.isSidebarHidden;

  ngOnInit(): void {
    this.permissionService.loadUserPermissions().subscribe();
  }

  readonly filteredNavItems = computed(() => {
    // Touch permissions signal to trigger reactivity when user permissions load
    this.permissionService.permissions();

    return ENTERPRISE_NAV_ITEMS.filter((item) => {
      switch (item.id) {
        case 'dashboard':
          return this.permissionService.can('dashboard.view');
        case 'tickets':
          return this.permissionService.can('ticket.view');
        case 'departments':
          return this.permissionService.can('department.view') || this.permissionService.can('department.manage');
        case 'projects':
          return this.permissionService.can('project.view') || this.permissionService.can('project.manage');
        case 'users':
          return this.permissionService.can('user.view') || this.permissionService.can('user.manage');
        case 'roles':
          return (
            this.permissionService.can('permission.view') ||
            this.permissionService.can('permission.manage')
          );
        case 'settings':
          return this.permissionService.can('settings.manage');
        default:
          return true;
      }
    });
  });

  readonly expandedGroupMap = signal<Record<string, boolean>>({ tickets: true });

  toggleGroup(groupId: string): void {
    this.expandedGroupMap.update(map => ({
      ...map,
      [groupId]: !map[groupId]
    }));
  }

  isGroupExpanded(groupId: string): boolean {
    return !!this.expandedGroupMap()[groupId];
  }

  onToggleCollapse(): void {
    this.layoutService.toggleSidebarCollapse();
  }

  onHide(): void {
    this.layoutService.hideSidebar();
  }

  onNavClick(): void {
    if (window.innerWidth < 768) {
      this.layoutService.hideSidebar();
    }
  }
}
